import { toast } from '@common/ui'
import { useCallback, useMemo, useRef, useState } from 'react'
import { com, kotlin, Nullable } from '@eidu/web'
import { useNavigate } from 'react-router-dom'
import DraftFieldValue, {
  draftFieldValuesToFieldValues,
  fieldValueToString,
} from '../../../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../../../util/EqualityHashMap'
import { requireNotUndefinedOrNull } from '../../../../util/require'
import createEntityToCreate from '../../createEntityToCreate'
import hasFieldError from '../../../../domain/entity/hasFieldError'
import { useCreateEntity, useDeleteEntity, useModifyEntity } from '../../../../io/useEntityMutations'
import { useFetchEntities } from '../../../../io/useEntities'
import { ConflictError } from '../../../../io/api/entity/data/postEntities'
import EntityWithLabelAndRelated from '../../../../domain/entity/EntityWithLabelAndRelated'
import Link from '../../../../domain/entity/Link'
import getNonNullFieldsFromEntity from '../../../../util/entity/getNonNullFieldsFromEntity'
import useHasPermission from '../../../permission/useHasPermission'
import FieldId = com.eidu.entity.field.FieldId
import EntityId = com.eidu.entity.EntityId
import EntityType = com.eidu.entity.type.EntityType
import EntityAction = com.eidu.entity.permission.EntityAction
import ExternalValueKey = com.eidu.entity.user.ExternalValueKey
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import validateFieldValues = com.eidu.entity.validation.validateFieldValues
import KtMap = kotlin.collections.KtMap

type UseEntityEditParams = {
  entity: EntityWithLabelAndRelated | undefined
  entityId: EntityId | undefined
  entityType: EntityType
}

type UseEntityEditResult = {
  fields: ReadonlyMap<FieldId, DraftFieldValue>
  updateField: (value: DraftFieldValue) => void
  submitting: boolean
  hasAttemptedSubmit: boolean
  valid: boolean
  submitEntity: () => Promise<void>
  deleteEntity: () => Promise<void>
  hasPermissions: boolean
  permissionsLoading: boolean
  registerExternalValues: (values: Map<ExternalValueKey, Nullable<string>>) => void
  registerValidation: (validate: () => boolean) => void
  registerPreSubmit: (fn: () => Promise<Link[]>) => void
  registerPostSubmit: (fn: (entityId: EntityId) => Promise<void>) => void
}

const useEntityEdit = ({ entity, entityId, entityType }: UseEntityEditParams): UseEntityEditResult => {
  const createMutation = useCreateEntity()
  const modifyMutation = useModifyEntity()
  const deleteMutation = useDeleteEntity()
  const fetchEntities = useFetchEntities()
  // We allow all users to see the create page (no entityId), the backend will validate the permissions
  const { hasPermission: hasRequiredPermissions, loading: permissionsLoading } = entityId
    ? useHasPermission(entityId, EntityAction.UpdateEntity)
    : { hasPermission: true, loading: false }

  const navigate = useNavigate()

  const [submitting, setSubmitting] = useState(false)

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(() =>
    entity ? getNonNullFieldsFromEntity(entity.entity) : new Map()
  )
  const updateField = (value: DraftFieldValue) => setFields((it) => new EqualityHashMap(it).set(value.fieldId, value))

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const stateRef = useRef<{
    externalValues: Map<ExternalValueKey, Nullable<string>>
    validationFn: (() => boolean) | undefined
    preSubmitFn: (() => Promise<Link[]>) | undefined
    postSubmitFn: ((entityId: EntityId) => Promise<void>) | undefined
  }>({
    externalValues: new Map(),
    validationFn: undefined,
    preSubmitFn: undefined,
    postSubmitFn: undefined,
  })

  const registerExternalValues = useCallback((values: Map<ExternalValueKey, Nullable<string>>) => {
    values.forEach((value, key) => {
      stateRef.current.externalValues.set(key, value)
    })
  }, [])

  const registerValidation = useCallback((validate: () => boolean) => {
    stateRef.current.validationFn = validate
  }, [])

  const registerPreSubmit = useCallback((fn: () => Promise<Link[]>) => {
    stateRef.current.preSubmitFn = fn
  }, [])

  const registerPostSubmit = useCallback((fn: (entityId: EntityId) => Promise<void>) => {
    stateRef.current.postSubmitFn = fn
  }, [])

  const valid = useMemo(() => {
    if (!entityType) return false

    const fieldsValid = entityType.fields.asJsReadonlyArrayView().every((field) => {
      const fieldValue = fields.get(field.id) ?? undefined
      return !hasFieldError(field, fieldValue, new Map())
    })

    return fieldsValid
  }, [entityType, fields])

  const submitEntity = useCallback(async () => {
    const state = stateRef.current

    const validate = (): boolean => {
      if (state.validationFn) {
        return state.validationFn()
      }
      return true
    }

    const addCustomEditorEntityLinks = async (updatedLinks: Link[]) => {
      if (state.preSubmitFn) {
        const customEditorEntityLinks = await state.preSubmitFn()
        if (customEditorEntityLinks) {
          updatedLinks.push(...customEditorEntityLinks)
        }
      }
    }

    const getFieldValues = (): DraftFieldValue[] =>
      entity
        ? [...Array.from(fields.values())].filter((field) => {
            const originalValue = entity.entity.valuesByFieldId.asJsReadonlyMapView().get(field.fieldId)
            return !originalValue || fieldValueToString(originalValue) !== field.value
          })
        : [...Array.from(fields.values())]

    // The entity is already persisted once postSubmit runs, so a follow-up failure (e.g. the form-service dual-write)
    // must not be reported as a failed save — that would hide that the entity exists and needs a retry.
    const finishSubmit = async (savedEntityId: EntityId, successMessage: string) => {
      try {
        if (state.postSubmitFn) await state.postSubmitFn(savedEntityId)
        toast.positive(successMessage)
      } catch (e) {
        console.error(e)
        toast.caution(`${successMessage}, but its content could not be fully saved. Edit and save again to retry.`)
      }
      navigate(-1)
    }

    const createEntity = async (
      localEntityType: EntityType,
      fieldValues: readonly DraftFieldValue[],
      updatedLinks: readonly Link[],
      externalValues: Map<ExternalValueKey, Nullable<string>>
    ) => {
      try {
        const newEntityId = await createMutation.mutateAsync(
          createEntityToCreate(entityType.id, fieldValues, updatedLinks, externalValues || new Map())
        )
        await finishSubmit(newEntityId, `Created ${localEntityType.name}`)
      } catch (e) {
        console.error(e)
        if (e instanceof ConflictError) {
          toast.negative(`Entity login identifier already exists. Please check email and phone number.`)
        } else {
          toast.negative(`Failed to create ${localEntityType.name}`)
        }
      }
    }

    const modifyEntity = async (
      existingEntity: EntityWithLabelAndRelated,
      localEntityType: EntityType,
      fieldValues: readonly DraftFieldValue[],
      updatedLinks: readonly Link[],
      externalValues: Map<ExternalValueKey, Nullable<string>>
    ) => {
      try {
        await modifyMutation.mutateAsync({
          id: existingEntity.entity.id,
          patch: {
            valuesToUpdateByFieldId: draftFieldValuesToFieldValues(fieldValues),
            linksToUpdate: new Map(updatedLinks.map((link) => [link.key, link.value])),
            externalValuesToUpdate: externalValues,
          },
        })
        await finishSubmit(existingEntity.entity.id, `${localEntityType.name} updated`)
      } catch (e) {
        console.error(e)
        if (e instanceof ConflictError) {
          toast.negative(`Entity login identifier already exists. Please check email and phone number.`)
        } else {
          toast.negative(`Failed to update ${localEntityType.name}`)
        }
      }
    }

    setHasAttemptedSubmit(true)

    if (!validate()) return

    const allFieldValues = draftFieldValuesToFieldValues([...fields.values()])
    const nonNullFieldValues = new EqualityHashMap(
      [...allFieldValues.entries()].filter(
        (entry): entry is [FieldId, NonNullable<(typeof entry)[1]>] => entry[1] != null
      )
    )
    const getTypeId = async (id: EntityId): Promise<EntityTypeId | null> => {
      const entities = await fetchEntities([id])
      return entities.get(id)?.entity.typeId ?? null
    }
    const validationResult = await validateFieldValues(
      KtMap.fromJsMap(nonNullFieldValues),
      entityType.fields,
      getTypeId
    )
    if (validationResult.asJsReadonlyMapView().size > 0) {
      toast.negative('Please fix validation errors before saving.')
      return
    }

    setSubmitting(true)
    try {
      const updatedLinks: Link[] = []

      await addCustomEditorEntityLinks(updatedLinks)

      const localEntityType = requireNotUndefinedOrNull(entityType)
      const fieldValues = getFieldValues()

      if (!entity) {
        await createEntity(localEntityType, fieldValues, updatedLinks, state.externalValues)
      } else {
        await modifyEntity(entity, localEntityType, fieldValues, updatedLinks, state.externalValues)
      }
    } finally {
      setSubmitting(false)
    }
  }, [createMutation, modifyMutation, fetchEntities, navigate, entityType, entity, fields])

  const deleteEntity = useCallback(async () => {
    const localEntityType = requireNotUndefinedOrNull(entityType)
    try {
      await deleteMutation.mutateAsync(requireNotUndefinedOrNull(entityId))
      toast.positive(`Deleted ${localEntityType.name}`)
      navigate(`/entities/${localEntityType.id.toString()}`)
    } catch (e) {
      console.error(e)
      toast.negative(`Failed to delete ${localEntityType.name}`)
    }
  }, [deleteMutation, entityType, entityId, navigate])

  return {
    fields,
    updateField,
    submitting,
    hasAttemptedSubmit,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions: hasRequiredPermissions,
    permissionsLoading: permissionsLoading,
    registerExternalValues,
    registerValidation,
    registerPreSubmit,
    registerPostSubmit,
  }
}

export default useEntityEdit
